/**
 * Cross-Room Organizer Assignment Protocol
 *
 * Handles assigning participants as organizers for sector rooms from the command room.
 * Commanders can promote participants within a sector to organizer role.
 */

import * as ed from '@noble/ed25519'
import { sha512 } from '@noble/hashes/sha512'
import {
  uint8ArrayToBase64,
  base64ToUint8Array,
} from '../crypto/identity'

// Configure ed25519 to use sha512
ed.etc.sha512Sync = (...m) => sha512(ed.etc.concatBytes(...m))

/**
 * Cross-room organizer assignment message
 */
export interface CrossRoomOrganizerAssignment {
  type: 'cross_room_organizer_assignment'
  id: string
  commandRoomId: string
  sectorRoomId: string
  assigneePeerId: string
  assigneePublicKey: string
  assignedBy: string
  assignedByName: string
  commanderPublicKey: string
  timestamp: Date
  signature: string
}

/**
 * Create a cross-room organizer assignment
 */
export async function createOrganizerAssignment(
  commandRoomId: string,
  sectorRoomId: string,
  assigneePeerId: string,
  assigneePublicKey: string,
  assignedBy: string,
  assignedByName: string,
  commanderPrivateKey: Uint8Array,
  commanderPublicKey: Uint8Array
): Promise<CrossRoomOrganizerAssignment> {
  const id = crypto.randomUUID()
  const timestamp = new Date()

  // Create the payload to sign
  const payload = {
    type: 'cross_room_organizer_assignment',
    id,
    commandRoomId,
    sectorRoomId,
    assigneePeerId,
    assigneePublicKey,
    assignedBy,
    timestamp: timestamp.toISOString(),
  }

  const payloadBytes = new TextEncoder().encode(JSON.stringify(payload))
  const signature = await ed.signAsync(payloadBytes, commanderPrivateKey)

  return {
    type: 'cross_room_organizer_assignment',
    id,
    commandRoomId,
    sectorRoomId,
    assigneePeerId,
    assigneePublicKey,
    assignedBy,
    assignedByName,
    commanderPublicKey: uint8ArrayToBase64(commanderPublicKey),
    timestamp,
    signature: uint8ArrayToBase64(signature),
  }
}

/**
 * Verify an organizer assignment signature
 */
export async function verifyOrganizerAssignment(
  assignment: CrossRoomOrganizerAssignment
): Promise<boolean> {
  try {
    const payload = {
      type: 'cross_room_organizer_assignment',
      id: assignment.id,
      commandRoomId: assignment.commandRoomId,
      sectorRoomId: assignment.sectorRoomId,
      assigneePeerId: assignment.assigneePeerId,
      assigneePublicKey: assignment.assigneePublicKey,
      assignedBy: assignment.assignedBy,
      timestamp: assignment.timestamp instanceof Date
        ? assignment.timestamp.toISOString()
        : assignment.timestamp,
    }

    const payloadBytes = new TextEncoder().encode(JSON.stringify(payload))
    const signature = base64ToUint8Array(assignment.signature)
    const publicKey = base64ToUint8Array(assignment.commanderPublicKey)

    return await ed.verifyAsync(signature, payloadBytes, publicKey)
  } catch {
    return false
  }
}

/**
 * Check if an assignment is for a specific sector
 */
export function isAssignmentForSector(
  assignment: CrossRoomOrganizerAssignment,
  sectorRoomId: string
): boolean {
  return assignment.sectorRoomId === sectorRoomId
}

/**
 * Check if a peer is assigned as organizer by this assignment
 */
export function isAssignedOrganizer(
  assignment: CrossRoomOrganizerAssignment,
  peerId: string
): boolean {
  return assignment.assigneePeerId === peerId
}

/**
 * Serialize an organizer assignment for transmission
 */
export function serializeOrganizerAssignment(assignment: CrossRoomOrganizerAssignment): Uint8Array {
  const json = JSON.stringify({
    ...assignment,
    timestamp: assignment.timestamp instanceof Date
      ? assignment.timestamp.toISOString()
      : assignment.timestamp,
  })
  return new TextEncoder().encode(json)
}

/**
 * Deserialize an organizer assignment from transmission
 */
export function deserializeOrganizerAssignment(data: Uint8Array): CrossRoomOrganizerAssignment {
  const json = new TextDecoder().decode(data)
  const parsed = JSON.parse(json)

  return {
    ...parsed,
    timestamp: new Date(parsed.timestamp),
  }
}

/**
 * Get the most recent assignment for a sector
 * (Each sector can only have one organizer)
 */
export function getMostRecentAssignmentForSector(
  assignments: CrossRoomOrganizerAssignment[],
  sectorRoomId: string
): CrossRoomOrganizerAssignment | null {
  const sectorAssignments = assignments
    .filter((a) => a.sectorRoomId === sectorRoomId)
    .sort((a, b) => {
      const timeA = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime()
      const timeB = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime()
      return timeB - timeA
    })

  return sectorAssignments[0] || null
}

/**
 * Get all assignments made by a commander
 */
export function getAssignmentsByCommander(
  assignments: CrossRoomOrganizerAssignment[],
  commanderPeerId: string
): CrossRoomOrganizerAssignment[] {
  return assignments.filter((a) => a.assignedBy === commanderPeerId)
}

/**
 * Convert to CRDT entry format
 */
export function toOrganizerAssignmentEntry(
  assignment: CrossRoomOrganizerAssignment
): {
  id: string
  commandRoomId: string
  sectorRoomId: string
  assigneePeerId: string
  assigneePublicKey: string
  assignedBy: string
  commanderPublicKey: string
  timestamp: number
  signature: string
} {
  return {
    id: assignment.id,
    commandRoomId: assignment.commandRoomId,
    sectorRoomId: assignment.sectorRoomId,
    assigneePeerId: assignment.assigneePeerId,
    assigneePublicKey: assignment.assigneePublicKey,
    assignedBy: assignment.assignedBy,
    commanderPublicKey: assignment.commanderPublicKey,
    timestamp: assignment.timestamp instanceof Date
      ? assignment.timestamp.getTime()
      : new Date(assignment.timestamp).getTime(),
    signature: assignment.signature,
  }
}

/**
 * Convert from CRDT entry format
 */
export function fromOrganizerAssignmentEntry(
  entry: {
    id: string
    commandRoomId: string
    sectorRoomId: string
    assigneePeerId: string
    assigneePublicKey: string
    assignedBy: string
    commanderPublicKey: string
    timestamp: number
    signature: string
  },
  assignedByName: string = 'Commander'
): CrossRoomOrganizerAssignment {
  return {
    type: 'cross_room_organizer_assignment',
    id: entry.id,
    commandRoomId: entry.commandRoomId,
    sectorRoomId: entry.sectorRoomId,
    assigneePeerId: entry.assigneePeerId,
    assigneePublicKey: entry.assigneePublicKey,
    assignedBy: entry.assignedBy,
    assignedByName,
    commanderPublicKey: entry.commanderPublicKey,
    timestamp: new Date(entry.timestamp),
    signature: entry.signature,
  }
}
